import type { BackendAuthResponse, SessionUser, ScopeContext } from '../model/types';
import {
  constructAuthMessage,
  constructRawAuthMessage,
  getCurrentTimestamp,
  sign,
  signatureToBase64,
} from '@/shared/lib/crypto.ts';
import { DEFAULT_SESSION_EXPIRATION } from '@/shared/lib/auth-config.ts';
import { secureSessionStorage } from '@/shared/lib/storage.ts';
import { emitSessionExpired } from '@/shared/lib/session-events.ts';
import { DebugLogger } from '@/shared/lib/debug.ts';
import { acceptLanguage } from '@/shared/lib/accept-language.ts';
import {
  BackendValidationError,
  backendValidationErrorFrom,
  type BackendErrorResponse,
} from '@/shared/lib/backend-errors.ts';
import { UNREADABLE_RESPONSE_CODE } from '@/shared/lib/response-shape.ts';

// Always use the relative `/api` proxy: Vite dev server proxies it in
// development; nginx proxies it in production. The API is server-to-server: it
// returns no CORS headers and refuses the preflight, so a direct cross-origin
// request to the backend fails. It also bypasses the version prefix.
const API_BASE_URL = '/api';

// Development mode flag - set false to test a real backend connection
const DEV_MODE = false;

export class BackendApiClient {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || API_BASE_URL;
  }

  private async makeRequest(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    sessionId: string,
    body?: any,
    scopeContext?: ScopeContext,
    extraHeaders?: Record<string, string>
  ): Promise<BackendAuthResponse> {
    const url = `${this.baseUrl}${endpoint}`;
    try {
      // Get session user from secure storage
      const sessionUser = await this.getSessionUser(sessionId);
      if (!sessionUser) {
        emitSessionExpired();
        return {
          success: false,
          error: 'Session not found or expired'
        };
      }

      const headers = {
        ...(await this.createSessionAuthHeaders(sessionUser, body, scopeContext)),
        ...extraHeaders,
      };

      DebugLogger.request(method, url, headers, body);
      DebugLogger.debug('Request details', {
        url,
        method,
        endpoint,
        sessionId,
        scopeContext
      });

      const requestOptions: RequestInit = {
        method,
        headers,
        mode: 'cors', // Explicitly set CORS mode
        ...(body && method !== 'GET' && { body: JSON.stringify(body) })
      };

      const response = await fetch(url, requestOptions);
      return await this.readResponse(response, method, url, sessionId);
    } catch (error) {
      if (DEV_MODE && error instanceof TypeError && error.message.includes('fetch')) {
        console.warn('🚧 CORS error detected. Running in development mode...');
        return this.simulateBackendResponse(sessionId, endpoint, method, body);
      }
      return this.failureResponse(error, url, method);
    }
  }

  /**
   * One report per failed `fetch`, shared by the JSON and multipart paths so the same
   * degraded network state reads identically regardless of which request shape hit it.
   */
  private failureResponse(error: unknown, url: string, method: string): BackendAuthResponse {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      DebugLogger.error('Network error', { error: error.message, url, method });
      return {
        success: false,
        error: `Network Error: ${error.message}. The request to ${url} failed before a response arrived.`,
      };
    }
    DebugLogger.error('Request failed', { error, url, method });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }

  /**
   * Turns a `fetch` response into the client's `BackendAuthResponse` shape.
   *
   * Shared by the JSON path and the multipart path so both report a backend error
   * identically — a `BackendValidationError` the caller can hand to
   * `parseBackendErrors`, never a bare status string that would lose the per-field
   * `parameter` the forms map onto their inputs.
   */
  private async readResponse(
    response: Response,
    method: string,
    url: string,
    sessionId: string,
  ): Promise<BackendAuthResponse> {
    if (!response.ok) {
      let errorData: BackendErrorResponse;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { error: 'Unknown error' };
      }

      DebugLogger.response(method, url, response.status, response.statusText, errorData);
      DebugLogger.error('Backend error response', {
        status: response.status,
        statusText: response.statusText,
        errorData: JSON.stringify(errorData, null, 2),
        requestUrl: url,
        requestMethod: method,
        sessionId: sessionId
      });

      const fallbackMessage = `HTTP ${response.status}: ${response.statusText}`;
      const validationError = backendValidationErrorFrom(errorData, fallbackMessage);
      validationError.status = response.status;

      return {
        success: false,
        error: validationError.message,
        errorCode: validationError.code,
        validationError,
      };
    }

    let data: unknown;
    try {
      data = await response.json();
    } catch (error) {
      DebugLogger.error('Unreadable success response', {
        status: response.status,
        error: error instanceof Error ? error.message : String(error),
        requestUrl: url,
        requestMethod: method,
      });
      const validationError = new BackendValidationError(
        'Unreadable response body', UNREADABLE_RESPONSE_CODE,
      );
      validationError.status = response.status;
      return {
        success: false,
        error: validationError.message,
        errorCode: UNREADABLE_RESPONSE_CODE,
        validationError,
      };
    }
    DebugLogger.response(method, url, response.status, response.statusText, data);

    return {
      success: true,
      data
    };
  }

  /**
   * POSTs an already-composed `multipart/form-data` body.
   *
   * The body is signed and sent as the *same* string — see `shared/lib/multipart.ts`
   * for why it cannot be built with `FormData`. The header set comes from the same
   * `createSessionAuthHeaders` builder every JSON request uses; the `raw` argument is
   * what makes it sign the composed string verbatim and carry the caller's boundary in
   * `Content-Type` instead of the JSON default.
   */
  async postMultipart(
    endpoint: string,
    sessionId: string,
    multipart: { body: string; contentType: string },
    scopeContext?: ScopeContext,
  ): Promise<BackendAuthResponse> {
    const url = `${this.baseUrl}${endpoint}`;
    try {
      const sessionUser = await this.getSessionUser(sessionId);
      if (!sessionUser) {
        emitSessionExpired();
        return { success: false, error: 'Session not found or expired' };
      }

      const headers = await this.createSessionAuthHeaders(
        sessionUser,
        undefined,
        scopeContext,
        { body: multipart.body, contentType: multipart.contentType },
      );

      DebugLogger.request('POST', url, headers, '[multipart]');

      const response = await fetch(url, {
        method: 'POST',
        headers,
        mode: 'cors',
        body: multipart.body,
      });
      return await this.readResponse(response, 'POST', url, sessionId);
    } catch (error) {
      return this.failureResponse(error, url, 'POST');
    }
  }

  private simulateBackendResponse(
    sessionId: string,
    endpoint: string,
    method: string,
    body?: any
  ): BackendAuthResponse {
    console.log('🔧 Development mode: Simulating backend response');
    console.log('📝 Request details:', {
      endpoint,
      method,
      sessionId,
      body
    });
    
    // Simulate successful authentication for development
    return {
      success: true,
      data: {
        message: 'Development mode: Session-based authentication simulated successfully',
        endpoint,
        method,
        sessionId,
        timestamp: new Date().toISOString()
      }
    };
  }

  private async getSessionUser(sessionId: string): Promise<SessionUser | null> {
    try {
      DebugLogger.session('Getting session user', { sessionId });
      
      const sessionPrivateKey = await secureSessionStorage.getSessionKey(sessionId);
      
      if (!sessionPrivateKey) {
        DebugLogger.session('Session not found in storage', { sessionId });
        return null;
      }

      DebugLogger.session('Session found in storage', { sessionId });

      // For now, we'll create a basic SessionUser object
      // In a real implementation, you'd store more user info with the session
      const sessionUser = {
        id: 'tech@veltiz.com', // This should be retrieved from storage
        type: 'person',
        sessionId,
        sessionPrivateKey,
        expiresAt: new Date(Date.now() + DEFAULT_SESSION_EXPIRATION * 1000).toISOString()
      };

      DebugLogger.session('Session user created', { 
        sessionId, 
        userId: sessionUser.id,
        expiresAt: sessionUser.expiresAt 
      });

      return sessionUser;
    } catch (error) {
      DebugLogger.error('Failed to get session user', { sessionId, error });
      return null;
    }
  }

  /**
   * The one owner of the session header set. A JSON request passes `body` and lets
   * `constructAuthMessage` serialize it; a request whose body is already an exact
   * string (multipart) passes `raw` instead, so the signature covers the composed
   * string verbatim and `Content-Type` names the caller's boundary.
   */
  private async createSessionAuthHeaders(
    sessionUser: SessionUser,
    body?: any,
    scopeContext?: ScopeContext,
    raw?: { body: string; contentType: string },
  ): Promise<Record<string, string>> {
    const currentTime = getCurrentTimestamp();
    const message = raw
      ? constructRawAuthMessage(sessionUser.sessionId, currentTime, raw.body)
      : constructAuthMessage(sessionUser.sessionId, currentTime, body);
    const signature = sign(sessionUser.sessionPrivateKey, message);

    // Convert signature to base64 format
    const signatureBase64 = signatureToBase64(signature);

    const headers: Record<string, string> = {
      'Content-Type': raw?.contentType ?? 'application/json',
      'Access-Time': currentTime,
      'User-Id': sessionUser.sessionId, // Use session ID as User-Id for session-based requests
      'User-Type': 'session',
      'Access-Signature': signatureBase64,
      'Accept-Language': acceptLanguage(),
    };

    // Add scope headers if scope context is provided
    if (scopeContext?.scopeId && scopeContext?.scopeType) {
      headers['Scope-Id'] = scopeContext.scopeId;
      headers['Scope-Type'] = scopeContext.scopeType;
    }

    // Log authentication details for debugging. A raw body can be megabytes of file
    // content, so only the JSON path logs the signed message itself.
    console.log('🔐 Session authentication details:', {
      message: raw ? `[raw body, ${raw.body.length} chars]` : message,
      timestamp: currentTime,
      sessionId: sessionUser.sessionId,
      userId: sessionUser.id
    });

    return headers;
  }

  async createAuthHeaders(
    sessionId: string,
    body?: any,
    scopeContext?: ScopeContext,
  ): Promise<Record<string, string>> {
    const sessionUser = await this.getSessionUser(sessionId);
    if (!sessionUser) {
      emitSessionExpired();
      throw new Error('Session not found or expired');
    }
    return this.createSessionAuthHeaders(sessionUser, body, scopeContext);
  }

  async get(
    endpoint: string,
    sessionId: string,
    scopeContext?: ScopeContext,
    extraHeaders?: Record<string, string>,
  ): Promise<BackendAuthResponse> {
    return this.makeRequest(endpoint, 'GET', sessionId, undefined, scopeContext, extraHeaders);
  }

  async post(endpoint: string, sessionId: string, body: any, scopeContext?: ScopeContext): Promise<BackendAuthResponse> {
    return this.makeRequest(endpoint, 'POST', sessionId, body, scopeContext);
  }

  async put(endpoint: string, sessionId: string, body: any, scopeContext?: ScopeContext): Promise<BackendAuthResponse> {
    return this.makeRequest(endpoint, 'PUT', sessionId, body, scopeContext);
  }

  async patch(endpoint: string, sessionId: string, body: any, scopeContext?: ScopeContext): Promise<BackendAuthResponse> {
    return this.makeRequest(endpoint, 'PATCH', sessionId, body, scopeContext);
  }

  async delete(endpoint: string, sessionId: string, scopeContext?: ScopeContext): Promise<BackendAuthResponse> {
    return this.makeRequest(endpoint, 'DELETE', sessionId, undefined, scopeContext);
  }
}

// Export a default instance
export const backendApiClient = new BackendApiClient();
